import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProduct } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { Package, ShoppingCart, Truck, ChevronRight, Minus, Plus, Lock } from 'lucide-react';

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProduct(id).then(r => { setProduct(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  const handleAddToCart = async () => {
    if (!user) return;
    setAdding(true);
    try { await addToCart(product.id, qty); } catch {}
    setAdding(false);
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-16 animate-pulse">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="aspect-square bg-[#F8F9FA]" />
        <div className="space-y-4"><div className="h-4 bg-[#F8F9FA] w-24" /><div className="h-8 bg-[#F8F9FA] w-full" /><div className="h-6 bg-[#F8F9FA] w-32" /></div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center">
      <Package size={48} className="mx-auto text-black/10 mb-4" />
      <p className="text-lg font-bold">Produit non trouvé</p>
      <Link to="/catalog" className="text-sm text-[#00A5FF] hover:underline mt-2 inline-block">Retour au catalogue</Link>
    </div>
  );

  return (
    <div data-testid="product-detail-page" className="min-h-screen">
      {/* Breadcrumb */}
      <div className="border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-xs text-black/50">
          <Link to="/" className="hover:text-[#00A5FF]">Accueil</Link>
          <ChevronRight size={12} />
          <Link to="/catalog" className="hover:text-[#00A5FF]">Catalogue</Link>
          <ChevronRight size={12} />
          <Link to={`/catalog?category=${product.category_slug}`} className="hover:text-[#00A5FF]">{product.category}</Link>
          <ChevronRight size={12} />
          <span className="text-[#111] font-medium truncate max-w-[200px]">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Image */}
          <div className="aspect-square bg-[#F8F9FA] border border-black/10 flex items-center justify-center overflow-hidden">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <Package size={80} className="text-black/10" />
            )}
          </div>

          {/* Info */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#00A5FF] font-semibold mb-2">{product.category}</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tight mb-2">{product.name}</h1>
            <p className="text-xs text-black/40 mb-4">SKU: {product.sku}</p>

            {product.description && (
              <p className="text-sm text-black/60 leading-relaxed mb-6">{product.description}</p>
            )}

            {/* Pricing */}
            <div className="border border-black/10 p-6 mb-6">
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-wider text-black/50 font-semibold">Prix public</span>
                  <span className="text-2xl font-extrabold text-[#111]">{product.public_price?.toFixed(2)} <span className="text-sm font-medium text-black/50">EUR</span></span>
                </div>
                {product.pro_price ? (
                  <div className="flex items-baseline justify-between pt-3 border-t border-black/10">
                    <span className="text-xs uppercase tracking-wider text-[#F5C400] font-semibold flex items-center gap-1">
                      Prix professionnel
                    </span>
                    <span className="text-2xl font-extrabold text-[#00A5FF]">{product.pro_price.toFixed(2)} <span className="text-sm font-medium text-black/50">EUR</span></span>
                  </div>
                ) : (
                  <div className="pt-3 border-t border-black/10 flex items-center gap-2 text-sm text-black/40">
                    <Lock size={14} />
                    <span>Connectez-vous pour voir votre prix professionnel</span>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery */}
            <div className="flex items-center gap-2 text-sm text-black/60 mb-6">
              <Truck size={16} className="text-[#00A5FF]" />
              <span>Délai de livraison: <strong>{product.delivery_delay}</strong></span>
            </div>

            {/* Add to Cart */}
            {user ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center border border-black/15">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#F8F9FA] transition-colors duration-200"><Minus size={14} /></button>
                  <span className="px-4 text-sm font-medium min-w-[40px] text-center">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#F8F9FA] transition-colors duration-200"><Plus size={14} /></button>
                </div>
                <button
                  onClick={handleAddToCart}
                  disabled={adding}
                  data-testid="add-to-cart-button"
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#00A5FF] text-white font-semibold text-sm rounded-sm hover:bg-[#0090e0] hover:-translate-y-0.5 disabled:opacity-50 transition-all duration-200"
                >
                  <ShoppingCart size={16} />
                  {adding ? 'Ajout...' : 'Ajouter au panier'}
                </button>
              </div>
            ) : (
              <div className="border border-[#00A5FF]/30 bg-[#00A5FF]/5 p-4 text-center">
                <p className="text-sm text-[#111] mb-2">Connectez-vous pour commander</p>
                <Link to="/login" className="text-sm font-semibold text-[#00A5FF] hover:underline">Se connecter</Link>
                <span className="text-sm text-black/30 mx-2">ou</span>
                <Link to="/register" className="text-sm font-semibold text-[#00A5FF] hover:underline">Créer un compte</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
